import { AuthError } from "@supabase/supabase-js";

/**
 * Auth error codes that indicate an invalid/expired session.
 * These should be handled gracefully (sign out + redirect) rather than
 * being tracked as errors in PostHog.
 *
 * Reference: https://supabase.com/docs/reference/javascript/auth-error-codes
 */
const AUTH_SESSION_ERROR_CODES = [
  "bad_jwt",
  // Note: "invalid_credentials" is intentionally excluded - it's a login failure,
  // not a session error. We want those tracked in PostHog.
  "session_not_found",
  "user_not_found",
  "refresh_token_not_found",
  "invalid_refresh_token",
];

/**
 * Error messages that indicate session/JWT issues.
 * Used as fallback when error code is not available.
 */
const AUTH_SESSION_ERROR_MESSAGES = [
  "Invalid JWT",
  "InvalidJWTToken",
  "JWT expired",
  "JWT claim",
  // Note: "invalid claim" removed - too broad, could match non-auth errors.
  // "JWT claim" and "session_id claim" cover the Supabase-specific cases.
  "token is expired",
  "session_id claim",
  "refresh_token_not_found",
  "Invalid Refresh Token",
  "User from sub claim",
  "Auth session missing",
];

/**
 * Checks if an error is an auth-related error that indicates
 * an invalid or expired session. These errors are expected when:
 * - User switches accounts
 * - Session becomes stale
 * - JWT expires
 * - User is deleted/disabled
 *
 * These should NOT be tracked as errors in PostHog - they are
 * expected user flows that should result in a graceful sign-out.
 */
export function isSessionError(error: unknown): boolean {
  if (!error) return false;

  // Check if it's a Supabase AuthError with a known code
  if (isAuthError(error)) {
    const code = error.code;
    if (code && AUTH_SESSION_ERROR_CODES.includes(code)) {
      return true;
    }
  }

  // Check error message as fallback (for errors without codes)
  const message = getErrorMessage(error);
  if (message) {
    const matchesSessionPattern = AUTH_SESSION_ERROR_MESSAGES.some((pattern) =>
      message.toLowerCase().includes(pattern.toLowerCase()),
    );
    if (matchesSessionPattern) {
      return true;
    }
  }

  // Note: We intentionally DON'T check for generic 401 status here.
  // 401s can come from many sources (external APIs, microservices, etc.)
  // and treating all of them as session errors would cause unintended sign-outs.
  // We rely on the specific error codes and message patterns above.

  return false;
}

/**
 * Type guard for Supabase AuthError
 */
function isAuthError(error: unknown): error is AuthError {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "AuthApiError"
  );
}

/**
 * Extract error message from various error types
 */
function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  if (typeof error === "string") {
    return error;
  }
  return null;
}

// Note: We removed hasStatus() - generic 401 checking was too broad
// and could cause unintended sign-outs from non-auth API failures.
